import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { RichEditor } from "@/components/admin/RichEditor";
import { formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIES = ["Market Insights", "Commercial Real Estate", "Property Management Tips", "Mombasa Market Analysis", "Tenant Spotlights", "Company News"];

const EMPTY_ARTICLE = {
  title: "", category: "Company News", excerpt: "", bodyHtml: "",
  image: "", author: "SBS Properties Team", published_at: "", read_time: "3 min read",
};

const Label = ({ children }) => (
  <label className="block text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1.5">{children}</label>
);

export const NewsManager = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_ARTICLE);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/news");
      setArticles(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_ARTICLE); setDrawerOpen(true); };
  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({ ...EMPTY_ARTICLE, ...a, bodyHtml: Array.isArray(a.body) ? a.body.map((p) => `<p>${p}</p>`).join("") : a.body || "" });
    setDrawerOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      category: form.category,
      excerpt: form.excerpt,
      body: form.bodyHtml,
      image: form.image,
      author: form.author,
      published_at: form.published_at,
      read_time: form.read_time,
    };
    try {
      if (editingId) {
        await api.put(`/admin/news/${editingId}`, payload);
        toast.success("Article updated.");
      } else {
        await api.post("/admin/news", payload);
        toast.success("Article published.");
      }
      setDrawerOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/news/${a.id}`);
      toast.success("Article deleted.");
      setArticles((prev) => prev.filter((x) => x.id !== a.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="news-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{articles.length} article{articles.length === 1 ? "" : "s"} on the news feed</p>
        <button
          data-testid="admin-add-article-button"
          onClick={openCreate}
          className="flex items-center gap-2 bg-brand px-5 py-3 text-xs font-mono uppercase tracking-[0.15em] font-bold text-ivory hover:bg-brand-hover transition-colors"
        >
          <Plus className="h-4 w-4" /> Add Article
        </button>
      </div>

      <div className="mt-6 border hairline bg-white overflow-x-auto" data-testid="admin-news-table">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b hairline text-left text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
              <th className="px-5 py-4">Article</th>
              <th className="px-5 py-4">Category</th>
              <th className="px-5 py-4">Published</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <tr key={a.id} className="border-b hairline last:border-0 hover:bg-ivory/60 transition-colors" data-testid={`admin-news-row-${a.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {a.image
                      ? <img src={a.image} alt="" className="h-11 w-16 object-cover border hairline" />
                      : <div className="h-11 w-16 border hairline bg-sand" />}
                    <div className="font-medium max-w-[320px]">{a.title}</div>
                  </div>
                </td>
                <td className="px-5 py-3 text-muted-foreground">{a.category}</td>
                <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">{formatDate(a.published_at)}</td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`admin-news-edit-${a.id}`} onClick={() => openEdit(a)} className="border hairline p-2 text-muted-foreground hover:text-brand hover:border-brand transition-colors" aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`admin-news-delete-${a.id}`} onClick={() => remove(a)} className="border hairline p-2 text-muted-foreground hover:text-red-600 hover:border-red-300 transition-colors" aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && articles.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-muted-foreground">No articles yet — publish your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[90] flex justify-end" data-testid="admin-news-dialog">
          <div className="absolute inset-0 bg-obsidian/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div data-lenis-prevent className="relative h-full w-full max-w-2xl bg-ivory overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between bg-ivory/95 backdrop-blur border-b hairline px-8 py-5">
              <h2 className="font-serif text-2xl">{editingId ? "Edit Article" : "New Article"}</h2>
              <button data-testid="admin-news-close-button" onClick={() => setDrawerOpen(false)} className="p-2 text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={save} className="px-8 py-8 space-y-6">
              <div>
                <Label>Headline</Label>
                <Input data-testid="admin-news-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none bg-white" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Category</Label>
                  <select data-testid="admin-news-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-input bg-white px-3 py-2 text-sm h-9 outline-none">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Publish Date</Label>
                  <Input data-testid="admin-news-date-input" type="date" value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} className="rounded-none bg-white" />
                </div>
                <div>
                  <Label>Read Time</Label>
                  <Input data-testid="admin-news-readtime-input" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} placeholder="3 min read" className="rounded-none bg-white" />
                </div>
              </div>

              <div>
                <Label>Cover Photo</Label>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="admin-news-file-input" onChange={uploadImage} />
                {form.image ? (
                  <div className="relative inline-block">
                    <img src={form.image} alt="" className="w-full max-w-sm aspect-video object-cover border hairline" />
                    <button
                      type="button"
                      data-testid="admin-news-image-remove"
                      onClick={() => setForm((f) => ({ ...f, image: "" }))}
                      className="absolute -top-2 -right-2 bg-obsidian text-ivory rounded-full p-1"
                      aria-label="Remove image"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="admin-news-upload-button"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    className="flex w-full items-center justify-center gap-2 border-2 border-dashed border-brand/40 bg-brand/5 px-4 py-6 text-sm font-semibold text-brand hover:border-brand hover:bg-brand/10 transition-colors disabled:opacity-50"
                  >
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                    {uploading ? "Uploading…" : "Upload Cover Photo"}
                  </button>
                )}
              </div>

              <div>
                <Label>Summary (shown on cards)</Label>
                <Textarea data-testid="admin-news-excerpt-input" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="rounded-none bg-white" />
              </div>

              <div>
                <Label>Article Text — write, paste, or add photos & video</Label>
                <RichEditor
                  testId="admin-news-body-editor"
                  value={form.bodyHtml}
                  onChange={(html) => setForm((f) => ({ ...f, bodyHtml: html }))}
                  placeholder="Write or paste your article…"
                />
              </div>

              <div>
                <Label>Author</Label>
                <Input data-testid="admin-news-author-input" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="rounded-none bg-white" />
              </div>

              <div className="sticky bottom-0 bg-ivory/95 backdrop-blur border-t hairline -mx-8 px-8 py-5 flex justify-end gap-3">
                <button type="button" data-testid="admin-news-cancel-button" onClick={() => setDrawerOpen(false)} className="border hairline px-6 py-3 text-sm font-medium hover:border-foreground/40 transition-colors">
                  Cancel
                </button>
                <button data-testid="admin-news-save-button" disabled={saving} className="bg-brand px-8 py-3 text-sm font-semibold text-ivory hover:bg-brand-hover transition-colors disabled:opacity-50">
                  {saving ? "Saving…" : editingId ? "Save Changes" : "Publish Article"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
